import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Nfc } from 'lucide-react';
import {
  alreadyRecordedCardId,
  cardErrorDetail,
  cardSaveFailure,
  chipTypeForCarrier,
  encodedValidFromIso,
  getApiClient,
  useGetIssueCardPayload,
  useGetReplaceCardPayload,
  useIssueCard,
  useReaderPrecondition,
  useReplaceCard,
  useTranslation,
  writeCardImage,
} from '@bms/common';
import type { AccessCard, IssueCardParams, ReaderPreconditionState, ReplaceCardParams } from '@bms/common';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** One key written in this step. `outcomeUnknown` keys are recorded `pending` by Edge. */
export interface EncodedKey {
  cardId: string;
  cardUid: string;
  outcomeUnknown: boolean;
}

export interface KeyEncodeStepProps {
  /** The room the guest keys open. */
  roomCode: string;
  holderName: string | null;
  /** ISO instant: the keys' Valid until, the stay's expected checkout. */
  validUntil: string;
  /** Keys to write, 1–4. */
  total: number;
  /**
   * The number the first key written here gets, 1 by default: the room dialog
   * adds key 3 to a stay that has two, or replaces key 1 (F10).
   */
  firstKey?: number;
  /**
   * Called once: after the last key, on Finish with the keys written so far, or
   * with `[]` on Finish without a key.
   */
  onFinish: (keys: EncodedKey[]) => void;
  /** True while a card is being written, so the host can refuse to close mid-write. */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Replace this key instead of issuing a new one (F10): the card is written
   * through the replace route, which keeps the record's identity and revokes
   * the old key as lost in the same transaction. `total` is then 1.
   */
  replaces?: AccessCard | null;
  /** The words of the button that ends the step before any key: "Finish without a key" by default. */
  cancelLabel?: string;
  /** Cards an earlier pass already wrote: still on the reader, they are never written again. */
  writtenUids?: readonly string[];
}

/** The replace route's body for a written guest key; the old key is revoked as lost. */
function replaceParamsFor(params: IssueCardParams): ReplaceCardParams {
  return {
    cardUid: params.cardUid,
    chipType: params.chipType,
    holderName: params.holderName,
    validFrom: params.validFrom,
    // Absent keeps the old key's Valid until (cards.md §Replacement).
    ...(params.validUntil ? { validUntil: params.validUntil } : {}),
    roomCode: params.roomCode,
    retirePreviousUid: params.retirePreviousUid,
    writeReport: params.writeReport,
  };
}

/** Written to the card but not yet saved in Edge; Retry saves it without writing again. */
interface UnsavedKey {
  params: IssueCardParams;
  outcomeUnknown: boolean;
}

type Translate = ReturnType<typeof useTranslation>['t'];

/** Why the reader cannot write right now, as one sentence — or null when it can. */
export function readerNotReadySentence(precondition: ReaderPreconditionState, t: Translate): string | null {
  const status = precondition.readerSnapshot.status;
  if (status === 'disconnected') return t('checkIn.reader.disconnected');
  if (status === 'no-sam') return t('checkIn.reader.noSam');
  if (precondition.openDisabledReason) {
    return t('checkIn.reader.notReady', { reason: t(precondition.openDisabledReason) });
  }
  return null;
}

/**
 * The reader line the header badge knows (ready / no SAM / disconnected), in a
 * sentence, with the card's UID as soon as one is placed. Shown from step 1 of
 * check-in so the desk sees the reader before it needs it.
 */
export function ReaderStatusLine({ precondition }: { precondition: ReaderPreconditionState }) {
  const { t } = useTranslation();
  const notReady = readerNotReadySentence(precondition, t);
  const uid = precondition.readerSnapshot.activePiccReader ? precondition.lastCard?.card.uid : undefined;
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-ui',
        notReady ? 'border-warning/40 bg-warning/10 text-warning-text' : 'border-border bg-muted/40 text-muted-foreground',
      )}
    >
      <Nfc className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {notReady
          ?? (uid
            ? t('checkIn.reader.readyWithCard', { uid })
            : t('checkIn.reader.ready'))}
      </span>
    </div>
  );
}

const errorDetail = (error: unknown, t: Translate): string => cardErrorDetail(error, t);

/**
 * Write the stay's guest keys one card at a time (audit P3 step 2).
 *
 * A fresh card on the reader is written as the next key without a click — the
 * desk is in this step to encode the cards it is placing, and a card already
 * written here, or the one whose write just failed, is never written again on
 * its own. A failure stops the step on the error with Retry; nothing closes.
 * The payload → write → persist order and the write-report rule are the shared
 * `writeCardImage` ones `IssueCardModal` uses.
 */
export function KeyEncodeStep({
  roomCode,
  holderName,
  validUntil,
  total,
  firstKey = 1,
  onFinish,
  onBusyChange,
  replaces,
  cancelLabel,
  writtenUids,
}: KeyEncodeStepProps) {
  const { t } = useTranslation();
  const precondition = useReaderPrecondition(true);
  const getIssuePayload = useGetIssueCardPayload();
  const getReplacePayload = useGetReplaceCardPayload();
  const issueCard = useIssueCard();
  const replaceCard = useReplaceCard();
  const replacedId = replaces?.id ?? null;
  const [keys, setKeys] = useState<EncodedKey[]>([]);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unsaved, setUnsaved] = useState<UnsavedKey | null>(null);
  // The UID last attempted: a failed card is retried only on Retry, never by the
  // auto-write effect while it still lies on the reader.
  const [attemptedUid, setAttemptedUid] = useState<string | null>(null);
  // The card being written by the effect below: set while rendering, when the write starts.
  const [job, setJob] = useState<{ readerId: string; cardUid: string; chipType: NonNullable<ReturnType<typeof chipTypeForCarrier>> } | null>(null);
  const [finished, setFinished] = useState(false);
  const finishedRef = useRef(false);
  // Set synchronously, so an effect that fires twice before a re-render (React's
  // StrictMode does exactly that on mount) can never start a second write.
  const busyRef = useRef(false);

  const reader = precondition.readerSnapshot.activePiccReader;
  const card = reader ? precondition.lastCard?.card : undefined;
  const cardUid = card?.uid;
  const chipType = chipTypeForCarrier(card?.carrier);
  const notReady = readerNotReadySentence(precondition, t);
  const alreadyKey = !!cardUid && (keys.some((key) => key.cardUid === cardUid) || !!writtenUids?.includes(cardUid));
  const next = firstKey + keys.length;
  // The stay's key count once this step is done: "Place key 3 of 3".
  const shownTotal = firstKey - 1 + total;

  useEffect(() => { onBusyChange?.(writing); }, [onBusyChange, writing]);

  const finish = useCallback((written: EncodedKey[]) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinished(true);
    onFinish(written);
  }, [onFinish]);

  const persist = useCallback(async (key: UnsavedKey, written: EncodedKey[]) => {
    let saved: AccessCard;
    try {
      saved = replacedId
        ? await replaceCard.mutateAsync({ cardId: replacedId, params: { ...replaceParamsFor(key.params), reason: 'lost' } })
        : await issueCard.mutateAsync(key.params);
    } catch (persistError) {
      const cardId = alreadyRecordedCardId(persistError);
      if (!cardId) throw persistError;
      saved = await getApiClient().getAccessCard(cardId);
    }
    setUnsaved(null);
    const nextKeys = [...written, { cardId: saved.id, cardUid: key.params.cardUid, outcomeUnknown: key.outcomeUnknown }];
    setKeys(nextKeys);
    if (nextKeys.length >= total) finish(nextKeys);
  }, [finish, issueCard, replaceCard, replacedId, total]);

  // A fresh card placed while the step waits is the next key. The write starts
  // while rendering — "writing" shows from that render — and runs in the effect.
  if (
    !finished && !writing && !error && !unsaved && !notReady && reader && cardUid && chipType
    && !alreadyKey && cardUid !== attemptedUid && keys.length < total
  ) {
    setAttemptedUid(cardUid);
    setWriting(true);
    setError(null);
    setJob({ readerId: reader.id, cardUid, chipType });
  }

  useEffect(() => {
    if (!job || busyRef.current) return;
    busyRef.current = true;
    void (async () => {
      const params: IssueCardParams = {
        cardUid: job.cardUid,
        chipType: job.chipType,
        cardType: 'guest',
        roomCode,
        validFrom: new Date().toISOString(),
        validUntil,
        ...(holderName ? { holderName } : {}),
        // A returned card still holds its revoked record; Edge retires it in the
        // issue transaction and refuses a live one (BMS-spec cards.md, UID reuse).
        retirePreviousUid: true,
      };
      let written = false;
      let outcomeUnknown = false;
      try {
        const payload = replacedId
          ? await getReplacePayload.mutateAsync({ cardId: replacedId, params: replaceParamsFor(params) })
          : await getIssuePayload.mutateAsync(params);
        const result = await writeCardImage(job.readerId, precondition.readerSnapshot.claimId, payload, t);
        if (!result.ok) {
          setError(result.message);
          return;
        }
        written = true;
        outcomeUnknown = result.outcomeUnknown;
        const key: UnsavedKey = {
          outcomeUnknown: result.outcomeUnknown,
          params: {
            ...params,
            cardUid: result.writeReport.cardUid,
            chipType: result.writeReport.chipType,
            writeReport: result.writeReport,
            validFrom: encodedValidFromIso(payload.payload),
          },
        };
        setUnsaved(key);
        await persist(key, keys);
      } catch (encodeError) {
        const detail = errorDetail(encodeError, t);
        if (!written) {
          setError(detail);
        } else {
          const failure = cardSaveFailure(t, encodeError, detail, { outcomeUnknown });
          // A refused write report is not cured by saving again: Retry encodes afresh.
          if (!failure.retrySave) setUnsaved(null);
          setError(failure.message);
        }
      } finally {
        busyRef.current = false;
        setWriting(false);
        setJob(null);
      }
    })();
  }, [job, getIssuePayload, getReplacePayload, holderName, keys, persist, precondition.readerSnapshot.claimId, replacedId, roomCode, t, validUntil]);

  const retrySave = useCallback(async () => {
    if (!unsaved || busyRef.current) return;
    busyRef.current = true;
    setWriting(true);
    setError(null);
    try {
      await persist(unsaved, keys);
    } catch (persistError) {
      const failure = cardSaveFailure(t, persistError, errorDetail(persistError, t), {
        outcomeUnknown: unsaved.outcomeUnknown,
        stillFailing: true,
      });
      if (!failure.retrySave) setUnsaved(null);
      setError(failure.message);
    } finally {
      busyRef.current = false;
      setWriting(false);
    }
  }, [keys, persist, t, unsaved]);

  // Retry writes the card on the reader again: the render above starts it.
  const retryEncode = () => {
    setAttemptedUid(null);
    setError(null);
  };

  let prompt: string;
  if (writing) prompt = t('checkIn.keys.writing', { n: next, total: shownTotal, uid: cardUid ?? '' });
  else if (alreadyKey) prompt = t('checkIn.keys.swapCard', { n: next - 1, total: shownTotal });
  else if (cardUid && !chipType) prompt = t('checkIn.keys.unsupportedCard', { uid: cardUid });
  else if (replaces) prompt = t('checkIn.keys.placeReplacement', { n: firstKey });
  else prompt = t('checkIn.keys.placeCard', { n: next, total: shownTotal });

  return (
    <div className="grid gap-4">
      <ReaderStatusLine precondition={precondition} />

      {notReady ? (
        <p className="text-ui">{t('checkIn.keys.notReadyHint')}</p>
      ) : (
        <p className="flex items-center gap-2 text-body font-medium" aria-live="polite">
          {writing && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {prompt}
        </p>
      )}

      {keys.length > 0 && (
        <ul className="grid gap-1" aria-label={t('checkIn.keys.writtenAria')}>
          {keys.map((key, index) => (
            <li
              key={key.cardId}
              title={key.cardUid}
              className={cn('flex items-center gap-2 text-ui', key.outcomeUnknown ? 'text-warning-text' : 'text-muted-foreground')}
            >
              {key.outcomeUnknown
                ? <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                : <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />}
              {key.outcomeUnknown
                ? t('checkIn.keys.keyPending', { n: firstKey + index })
                : t('checkIn.keys.keyWritten', { n: firstKey + index })}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div role="alert" className="grid gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-ui text-destructive">
          <p className="flex items-start gap-2">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
          <div>
            <Button
              size="sm"
              variant="outline"
              disabled={writing || (!unsaved && (!cardUid || !chipType || alreadyKey))}
              onClick={() => { if (unsaved) void retrySave(); else retryEncode(); }}
            >
              {t('checkIn.keys.retry')}
            </Button>
            {!unsaved && !cardUid && (
              <span className="ml-2 text-caption text-muted-foreground">{t('checkIn.keys.retryNeedsCard')}</span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {keys.length === 0 ? (
          <Button variant="outline" disabled={writing || !!unsaved} onClick={() => finish([])}>
            {cancelLabel ?? t('checkIn.keys.finishWithoutKey')}
          </Button>
        ) : (
          <Button variant="outline" disabled={writing || !!unsaved} onClick={() => finish(keys)}>
            {t('checkIn.keys.finishWith', { count: keys.length })}
          </Button>
        )}
      </div>
    </div>
  );
}
